import type { datadogRum } from '@datadog/browser-rum';
import { getConsentSnapshot, subscribeConsent } from '@/contexts/consent/store';
import { LOCALE_CONFIG } from '@/contexts/i18n/utils';

type Rum = Pick<typeof datadogRum, 'addDurationVital'>;
type CorePart = 'header' | 'prompt';

const homePaths = new Set(Object.values(LOCALE_CONFIG).map(({ pathPrefix }) => pathPrefix || '/'));
let initialPath: string | null = null;
let state: 'idle' | 'loading' | 'ready' | 'finished' = 'idle';
let duration: number | null = null;
let rum: Rum | null = null;
let cleanup: (() => void) | null = null;
let wasGranted = false;
const parts = new Set<CorePart>();

function normalizePath(pathname: string): string {
    return pathname.replace(/\/+$/, '') || '/';
}

function discard(): void {
    state = 'finished';
    duration = null;
    cleanup?.();
    cleanup = null;
}

function isCurrentTarget(): boolean {
    return initialPath === normalizePath(window.location.pathname);
}

function checkConsent(): 'granted' | 'pending' | 'discarded' {
    const consent = getConsentSnapshot();
    if (consent.analyticsAllowed) {
        wasGranted = true;
        return 'granted';
    }
    if (
        !wasGranted
        && consent.cookieAvailable
        && (consent.resolution === 'pending'
            || (consent.resolution === 'resolved' && consent.data?.hasChoice === false))
    ) return 'pending';
    discard();
    return 'discarded';
}

function flush(): void {
    if (state !== 'ready') return;
    if (!isCurrentTarget() || document.visibilityState !== 'visible') {
        discard();
        return;
    }
    if (checkConsent() !== 'granted' || !rum || duration === null) return;
    const completedDuration = duration;
    discard();
    try {
        rum.addDurationVital('landing_page_ready', {
            startTime: performance.timeOrigin,
            duration: completedDuration,
            context: {
                page: initialPath,
                core_page: 'landing_page',
                measurement: 'initial_load',
                metric_version: 1,
            },
        });
    } catch {
        console.warn('[datadog-rum] landing ready measurement failed');
    }
}

/** 由既有框架入口早期启动；Feature 挂载不能把 SPA 到达变成文档首访。 */
export function initializeLandingPageReady(): void {
    if (typeof window === 'undefined' || state !== 'idle') return;
    const navigation = performance.getEntriesByType('navigation')[0] as (PerformanceNavigationTiming & { activationStart?: number }) | undefined;
    const lifecycleDocument = document as Document & { prerendering?: boolean; wasDiscarded?: boolean };
    initialPath = normalizePath(new URL(navigation?.name || window.location.href).pathname);
    if (
        !homePaths.has(initialPath)
        || !isCurrentTarget()
        || document.visibilityState !== 'visible'
        || lifecycleDocument.prerendering
        || lifecycleDocument.wasDiscarded
        || (navigation?.activationStart ?? 0) > 0
        || performance.getEntriesByType('visibility-state').some((entry) => entry.name === 'hidden')
        || !Number.isFinite(performance.timeOrigin)
        || performance.timeOrigin <= 0
    ) {
        discard();
        return;
    }
    state = 'loading';
    const onVisibility = () => {
        if (document.visibilityState !== 'visible') discard();
    };
    const onLocation = () => {
        if (!isCurrentTarget()) discard();
    };
    const unsubscribeConsent = subscribeConsent(() => {
        checkConsent();
        // 先让既有同步订阅把 SDK 切到 granted，再交付本地锁定的成功时间。
        queueMicrotask(flush);
    });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', discard);
    window.addEventListener('popstate', onLocation);
    cleanup = () => {
        unsubscribeConsent();
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('pagehide', discard);
        window.removeEventListener('popstate', onLocation);
    };
    checkConsent();
}

/** 仅消费已初始化的现有 SDK，不导入、初始化或等待 SDK。 */
export function registerLandingPageReadyRum(sdk: Rum): void {
    rum = sdk;
    flush();
}

/** Next 的已有导航通知也用于丢弃离开首次目标的样本，query/hash 更新不影响。 */
export function onLandingPageReadyNavigation(url: string): void {
    if (state !== 'loading' && state !== 'ready') return;
    if (normalizePath(new URL(url, window.location.href).pathname) !== initialPath) discard();
}

/** 两个真实首屏交互点均 commit 后锁定一次成功时间，不受后续 SDK 等待影响。 */
export function markLandingPageReady(part: CorePart): void {
    if (state !== 'loading') return;
    if (!isCurrentTarget() || document.visibilityState !== 'visible') {
        discard();
        return;
    }
    if (checkConsent() === 'discarded') return;
    parts.add(part);
    if (parts.size !== 2) return;
    const completedDuration = performance.now();
    if (!Number.isFinite(completedDuration) || completedDuration < 0) {
        discard();
        return;
    }
    duration = completedDuration;
    state = 'ready';
    flush();
}
